import { compareVersion } from '../compare-version';

/**
 * 腾讯会议App
 */
export const isWeMeetApp = (userAgent: string) =>
  /app\/tencent_wemeet/i.test(userAgent.toLowerCase());

/**
 * Windows 系统
 */
export const isWin = (userAgent: string) => /windows|win32|win64/i.test(userAgent.toLowerCase());

/**
 * mac 系统
 */
export const isMac = (userAgent: string) => /macintosh|mac os x/i.test(userAgent.toLowerCase());

/**
 * 安卓
 */
export const isAndroid = (userAgent: string) => /android|adr/i.test(userAgent.toLowerCase());

/**
 * IOS
 */
export const isIOS = (userAgent: string) => /iphone|ipod|ipad/i.test(userAgent.toLowerCase());

/**
 * iPhone
 */
export const isIPhone = (userAgent: string) => /iphone/i.test(userAgent.toLowerCase());

/**
 * iPad
 */
export const isIPad = (userAgent: string) => /ipad/i.test(userAgent.toLowerCase());

/**
 * Windows App
 */
export const isWinApp = (userAgent: string) => isWin(userAgent) && isWeMeetApp(userAgent);

/**
 * mac App
 */
export const isMacApp = (userAgent: string) =>
  !isIOS(userAgent) && isMac(userAgent) && isWeMeetApp(userAgent);

/**
 * ie 10
 */
export const isIE10 = (navigator: Navigator) =>
  navigator.appName === 'Microsoft Internet Explorer' &&
  parseInt(navigator.appVersion.split(';')[1].replace(/[ ]/g, '').replace('MSIE', ''), 10) === 10;

/**
 * 微信浏览器
 */
export const isWeChat = (ua: string) => /micromessenger/i.test(ua) && !/wxwork/i.test(ua);
/**
 * 企业微信
 */
export const isWxwork = () => navigator.userAgent.toLowerCase().indexOf('wxwork') !== -1;

/*
 * 企业微信私有化
 */
export const isWxworkLocal = () => navigator.userAgent.toLowerCase().indexOf('wxworklocal') !== -1;
/**
 * Saas企业微信（非私有化企业微信）
 */
export const isWxWorkSaas = () => isWxwork() && !isWxworkLocal();
/**
 * Determine whether QBCore version of the web browser embedded in the WeiXin PC client
 * is less than the given parameter QBCoreVer.
 * @param QBCoreVer the QBCore version you want do compare against
 */
export const isWeiXinQBCoreLt = (QBCoreVer: any) => {
  const { userAgent } = window.navigator;
  const qbCoreInfo = userAgent.match(/QBCore\/([\d.]+)/i);
  const isWeiXin = /MicroMessenger/i.test(userAgent);
  return (
    isWeiXin &&
    !userAgent.includes('wxwork') &&
    qbCoreInfo &&
    qbCoreInfo.length >= 2 &&
    compareVersion(qbCoreInfo[1], QBCoreVer) < 0
  );
};
/**
 * 企业微信的userAgent：Mozilla/5.0 (Linux; Android 9; Pixel 2 Build/PQ2A.190405.003; wv)
 * AppleWebKit/537.36 (KHTML, like Gecko) Version/4.0 Chrome/66.0.3359.126
 * MQQBrowser/6.2 TBS/044605 Mobile Safari/537.36 wxwork/2.7.8 MicroMessenger/7.0.1 NetType/WIFI Language/zh
 */
export const isWeiXinGte656 = () => {
  const { userAgent } = window.navigator;
  const wxInfo = userAgent.match(/MicroMessenger\/([\d.]+)/i);
  return (
    !userAgent.includes('wxwork') &&
    wxInfo &&
    wxInfo.length >= 2 &&
    compareVersion(wxInfo[1], '6.5.6') >= 0
  );
};

/**
 * 企业微信版本是否大于3.1.2
 */
export const isWxWorkGte312 = () => {
  const { userAgent } = window.navigator;
  const wxInfo = userAgent.match(/wxwork\/([\d.]+)/i);
  console.log('>>>>wxInfo', wxInfo);
  return userAgent.includes('wxwork') && wxInfo && wxInfo.length >= 2 && wxInfo[1] >= '3.1.2';
};

export const getOsName = function () {
  /**
   * windows
   */
  if (navigator.appVersion.includes('Win')) {
    return 'windows';
  }

  if (navigator.appVersion.includes('Android')) {
    return 'android';
  }

  /**
   * ios
   */
  // iOS和Mac的顺序不能变，经测试发现Mac的浏览器里会有iOS的字样
  if (navigator.appVersion.includes('iPhone') || navigator.appVersion.includes('iPad')) {
    return 'ios';
  }

  /**
   * mac
   */
  const isMac =
    navigator.platform === 'Mac68K' ||
    navigator.platform === 'MacPPC' ||
    navigator.platform === 'Macintosh' ||
    navigator.platform === 'MacIntel' ||
    navigator.appVersion.includes('Mac');
  if (isMac) {
    return 'mac';
  }

  if (navigator.userAgent.includes('Linux')) {
    return 'linux';
  }

  return 'unknown';
};

/**
 * Linux 系统
 */
export const isLinux = () => {
  const os = getOsName();
  if (os === 'linux') {
    return true;
  }
  return false;
};

export const isWindowsWechat = () => /WindowsWechat/.test(navigator.userAgent);

export const isEdge = () => /Edge/.test(navigator.userAgent);

export const isFF = () => navigator.userAgent.toLowerCase().indexOf('firefox') !== -1;

export const isIE = () => /Trident/.test(navigator.userAgent);

export const isQQBrowser = () => /QQBrowser/.test(navigator.userAgent);

/**
 * 企业微信安卓版
 */
export const isWxworkInAndroid = (ua: string) => /Android/i.test(ua) && /wxwork/i.test(ua);

/**
 * 获取系统的版本号：windows、android、mac、ios
 */
export const getSystemVersion = () => {
  const os = getOsName();
  const { userAgent } = window.navigator;
  let info = null;

  if (os === 'windows') {
    info = userAgent.toLocaleLowerCase().match(/windows nt ([\d.]+)/i);
  } else if (os === 'android') {
    info = userAgent.toLocaleLowerCase().match(/android ([\d.]+)/i);
  } else if (os === 'mac') {
    info = userAgent
      .toLocaleLowerCase()
      .replace(/_/g, '.')
      .match(/mac os x ([\d.]+)/i);
  } else if (os === 'ios') {
    info = userAgent
      .toLocaleLowerCase()
      .replace(/_/g, '.')
      .match(/os ([\d.]+)/i);
  } else {
    return 'unknown';
  }

  return (info && info.length >= 2 && info[1]) || 'unknown';
};

/**
 * 获取设备语言，目前仅支持：繁体中文、简体中文、英语
 */
export const getSystemLanguage = () => {
  const language = navigator.language.toLocaleLowerCase();
  if (language === 'zh-tw' || language === 'zh-hk' || language === 'zh-mo') {
    return '繁体中文';
  }
  if (language.indexOf('zh') > -1) {
    return '简体中文';
  }
  if (language.indexOf('en') > -1) {
    return '英语';
  }
  return 'unknown';
};

/**
 * 获取系统时区
 */
export const getSystemTimeZone = () => {
  if (Intl) {
    const arr = new Date().toTimeString().split(' ');
    const str = arr[2].substring(1, arr[2].length - 1);
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone.split('/');
    return `(${arr[1]})${str}-${timeZone[1]}`;
  }
  return 'unknown';
};
/**
 * 手机qq环境
 */
export const isQQ = (ua?: string) => {
  const u = !ua ? navigator.userAgent.toLowerCase() : ua.toLowerCase();
  const res = u.match(/\sqq/i) !== null;
  // console.log('>>> QQ浏览器环境:', res);
  return res;
};

/**
 * huawei
 */
export const isHuawei = (ua: string) => /huawei|honor/i.test(ua.toLowerCase());

/**
 * 暂时没有实际能判断的ua，视为isIPad判断，可能无法准确判断
 */
export const isVisionPro = (ua: string) => /visionpro|vision/i.test(ua.toLowerCase()) || isIPad(ua);
